import clsx from 'clsx';
import type { ComponentProps, CSSProperties, ReactElement } from 'react';
import { __, sprintf, isRTL } from '@wordpress/i18n';
import { Spinner as WCSpinner, Popover } from '@wordpress/components';
import { useMergeRefs, useResizeObserver } from '@wordpress/compose';
import {
	useContext,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from '@wordpress/element';
import { isAppleOS } from '@wordpress/keycodes';
import { inertValue } from '@wordpress/react-inert-value';
import DataViewsContext from '../../dataviews-context';
import DataViewsSelectionCheckbox from '../../dataviews-selection-checkbox';
import ItemActions from '../../dataviews-item-actions';
import { MEDIA_ASPECT_RATIOS, sortValues } from '../../../constants';
import {
	useSomeItemHasAPossibleBulkAction,
	useHasAPossibleBulkAction,
	hasAPossibleBulkAction,
	BulkSelectionCheckbox,
	BulkActionToolbar,
} from '../../dataviews-bulk-actions';
import type {
	Action,
	MediaAspectRatio,
	NormalizedField,
	ViewTable as ViewTableType,
	ViewTableProps,
} from '../../../types';
import type { SetSelection } from '../../../types/private';
import ColumnHeaderMenu from './column-header-menu';
import ColumnPrimary from './column-primary';
import { useScrollState } from './use-scroll-state';
import getDataByGroup from '../utils/get-data-by-group';
import getTableColumns from '../utils/get-table-columns';
import useSelectionProps from '../utils/use-selection-props';
import { PropertiesSection } from '../../dataviews-view-config/properties-section';
import { useDelayedLoading } from '../../../hooks/use-delayed-loading';

function getEffectiveAlign(
	explicitAlign: 'start' | 'center' | 'end' | undefined,
	fieldType: string | undefined
): 'start' | 'center' | 'end' | undefined {
	if ( explicitAlign ) {
		return explicitAlign;
	}
	if ( fieldType === 'integer' || fieldType === 'number' ) {
		return 'end';
	}
	return undefined;
}

interface TableColumnFieldProps< Item > {
	fields: NormalizedField< Item >[];
	column: string;
	item: Item;
	align?: 'start' | 'center' | 'end';
}

interface TableRowProps< Item > {
	hasBulkActions: boolean;
	item: Item;
	level?: number;
	actions: Action< Item >[];
	fields: NormalizedField< Item >[];
	id: string;
	view: ViewTableType;
	titleField?: NormalizedField< Item >;
	mediaField?: NormalizedField< Item >;
	mediaAspectRatio?: MediaAspectRatio;
	descriptionField?: NormalizedField< Item >;
	selection: string[];
	getItemId: ( item: Item ) => string;
	onChangeSelection: SetSelection;
	onMouseDown: ( event: React.MouseEvent ) => void;
	onClickCapture: ( event: React.MouseEvent ) => void;
	isItemClickable: ( item: Item ) => boolean;
	onClickItem?: ( item: Item ) => void;
	renderItemLink?: (
		props: {
			item: Item;
		} & ComponentProps< 'a' >
	) => ReactElement;
	isActionsColumnSticky?: boolean;
	posinset?: number;
}

function TableColumnField< Item >( {
	item,
	fields,
	column,
	align,
}: TableColumnFieldProps< Item > ) {
	const field = fields.find( ( f ) => f.id === column );

	if ( ! field ) {
		return null;
	}

	const className = clsx( 'dataviews-view-table__cell-content-wrapper', {
		'dataviews-view-table__cell-align-end': align === 'end',
		'dataviews-view-table__cell-align-center': align === 'center',
	} );

	return (
		<div className={ className }>
			<field.render item={ item } field={ field } />
		</div>
	);
}

function TableRow< Item >( {
	hasBulkActions,
	item,
	level,
	actions,
	fields,
	id,
	view,
	titleField,
	mediaField,
	mediaAspectRatio,
	descriptionField,
	selection,
	getItemId,
	isItemClickable,
	onClickItem,
	renderItemLink,
	onChangeSelection,
	onMouseDown,
	onClickCapture,
	isActionsColumnSticky,
	posinset,
}: TableRowProps< Item > ) {
	const { paginationInfo } = useContext( DataViewsContext );
	const hasPossibleBulkAction = useHasAPossibleBulkAction( actions, item );
	const isSelected = hasPossibleBulkAction && selection.includes( id );
	const {
		showTitle = true,
		showMedia = true,
		showDescription = true,
		infiniteScrollEnabled,
	} = view;
	const columns = getTableColumns( view, fields );
	const hasPrimaryColumn =
		( titleField && showTitle ) ||
		( mediaField && showMedia ) ||
		( descriptionField && showDescription );

	return (
		<tr
			className={ clsx( 'dataviews-view-table__row', {
				'is-selected': hasPossibleBulkAction && isSelected,
				'has-bulk-actions': hasPossibleBulkAction,
			} ) }
			aria-setsize={
				infiniteScrollEnabled ? paginationInfo.totalItems : undefined
			}
			aria-posinset={ posinset }
			role={ infiniteScrollEnabled ? 'article' : undefined }
			onClickCapture={ onClickCapture }
			onMouseDown={ ( event ) => {
				// Firefox has a unique feature where ctrl/cmd + click selects a
				// table cell. This interferes with the bulk selection behavior,
				// so this code prevents it.
				const isMetaClick = isAppleOS() ? event.metaKey : event.ctrlKey;
				if (
					event.button === 0 &&
					isMetaClick &&
					window.navigator.userAgent
						.toLowerCase()
						.includes( 'firefox' )
				) {
					event.preventDefault();
				}
				onMouseDown( event );
			} }
		>
			{ hasBulkActions && (
				<td className="dataviews-view-table__checkbox-column">
					<div className="dataviews-view-table__cell-content-wrapper">
						<DataViewsSelectionCheckbox
							item={ item }
							selection={ selection }
							onChangeSelection={ onChangeSelection }
							getItemId={ getItemId }
							titleField={ titleField }
							disabled={ ! hasPossibleBulkAction }
						/>
					</div>
				</td>
			) }
			{ hasPrimaryColumn && (
				<td>
					<ColumnPrimary
						item={ item }
						level={ level }
						titleField={ showTitle ? titleField : undefined }
						mediaField={ showMedia ? mediaField : undefined }
						mediaAspectRatio={ mediaAspectRatio }
						descriptionField={
							showDescription ? descriptionField : undefined
						}
						isItemClickable={ isItemClickable }
						onClickItem={ onClickItem }
						renderItemLink={ renderItemLink }
					/>
				</td>
			) }
			{ columns.map( ( column: string ) => {
				// Explicit picks the supported styles.
				const { width, maxWidth, minWidth, align } =
					view.layout?.styles?.[ column ] ?? {};
				const field = fields.find( ( f ) => f.id === column );
				const effectiveAlign = getEffectiveAlign( align, field?.type );

				return (
					<td
						key={ column }
						style={ {
							width,
							maxWidth,
							minWidth,
						} }
					>
						<TableColumnField
							fields={ fields }
							item={ item }
							column={ column }
							align={ effectiveAlign }
						/>
					</td>
				);
			} ) }
			{ !! actions?.length && (
				// Disable reason: we are not making the element interactive,
				// but preventing any click events from bubbling up to the
				// table row. This allows us to add a click handler to the row
				// itself (to toggle row selection) without erroneously
				// intercepting click events from ItemActions.

				<td
					className={ clsx( 'dataviews-view-table__actions-column', {
						'dataviews-view-table__actions-column--sticky': true,
						'dataviews-view-table__actions-column--stuck':
							isActionsColumnSticky,
					} ) }
					onClick={ ( e ) => e.stopPropagation() }
				>
					<ItemActions item={ item } actions={ actions } />
				</td>
			) }
		</tr>
	);
}

function ViewTable< Item >( {
	actions,
	data,
	fields,
	getItemId,
	getItemLevel,
	isLoading = false,
	onChangeView,
	onChangeSelection,
	selection,
	setOpenedFilter,
	onClickItem,
	isItemClickable,
	renderItemLink,
	view,
	className,
	empty,
}: ViewTableProps< Item > ) {
	const { containerRef } = useContext( DataViewsContext );
	const isDelayedLoading = useDelayedLoading( isLoading );
	const groupField = view.groupBy?.field
		? fields.find( ( f ) => f.id === view.groupBy?.field )
		: null;
	const dataByGroup = groupField ? getDataByGroup( data, groupField ) : null;
	// When grouping is enabled the rendered order is by group rather than the
	// order of `data`; ranges follow what the user sees.
	const orderedData = dataByGroup
		? Array.from( dataByGroup.values() ).flat()
		: data;
	const { getSelectionProps } = useSelectionProps( {
		data: orderedData,
		getItemId,
		isItemSelectable: ( item ) => hasAPossibleBulkAction( actions, item ),
		selection,
		onChangeSelection,
		selectionMode: 'multi',
		shouldSelectOnClick: false,
	} );
	const headerMenuRefs = useRef<
		Map< string, { node: HTMLButtonElement; fallback: string } >
	>( new Map() );
	const headerMenuToFocusRef = useRef< HTMLButtonElement >( undefined );
	const [ nextHeaderMenuToFocus, setNextHeaderMenuToFocus ] =
		useState< HTMLButtonElement >();
	const [ contextMenuAnchor, setContextMenuAnchor ] = useState< {
		getBoundingClientRect: () => DOMRect;
	} | null >( null );

	useEffect( () => {
		if ( headerMenuToFocusRef.current ) {
			headerMenuToFocusRef.current.focus();
			headerMenuToFocusRef.current = undefined;
		}
	} );

	const tableNoticeId = useId();
	const tableHeaderRef = useRef< HTMLTableSectionElement >( null );
	const tableSelectionRef = useRef< HTMLSpanElement >( null );
	const bulkSelectionRef = useRef< HTMLSpanElement >( null );
	const bulkActionsRef = useRef< HTMLDivElement >( null );
	// Place the bulk actions toolbar over the header by writing to the DOM
	// directly: it is correct on first paint and skips a re-render. The sticky
	// offset is copied so the toolbar follows a header that consumers move.
	const syncBulkActionsPosition = () => {
		const header = tableHeaderRef.current;
		const overlay = bulkActionsRef.current;
		const win = header?.ownerDocument.defaultView;
		if ( header && overlay && win ) {
			overlay.style.setProperty(
				'--wp-dataviews-table-header-height',
				`${ header.getBoundingClientRect().height }px`
			);
			overlay.style.insetBlockStart =
				win.getComputedStyle( header ).insetBlockStart;
		}
	};
	const resizeHeaderRef = useResizeObserver< HTMLTableSectionElement >(
		syncBulkActionsPosition
	);
	const tableHeaderRefs = useMergeRefs( [ tableHeaderRef, resizeHeaderRef ] );
	const [ isActionInProgress, setIsActionInProgress ] = useState( false );
	const hasBulkActions = useSomeItemHasAPossibleBulkAction( actions, data );
	const showBulkActions =
		( hasBulkActions && selection.length > 0 ) || isActionInProgress;
	// The resize observer reports after the first paint, so measure on show.
	useLayoutEffect( syncBulkActionsPosition, [ showBulkActions ] );
	const hadSelectionRef = useRef( false );
	useEffect( () => {
		const tableHead = tableHeaderRef.current;
		const ownerDocument = tableHead?.ownerDocument;
		if (
			showBulkActions &&
			tableHead?.contains( ownerDocument?.activeElement ?? null )
		) {
			bulkSelectionRef.current?.focus();
		} else if (
			hadSelectionRef.current &&
			! showBulkActions &&
			( ownerDocument?.activeElement === ownerDocument?.body ||
				bulkActionsRef.current?.contains(
					ownerDocument?.activeElement ?? null
				) )
		) {
			tableSelectionRef.current?.focus();
		}
		hadSelectionRef.current = showBulkActions;
	}, [ showBulkActions ] );

	const { isHorizontalScrollEnd, isVerticallyScrolled } = useScrollState( {
		scrollContainerRef: containerRef,
		enabledHorizontal: !! actions?.length,
	} );

	const disableHeaderControls = showBulkActions;

	if ( nextHeaderMenuToFocus ) {
		// If we need to force focus, we short-circuit rendering here
		// to prevent any additional work while we handle that.
		// Clearing out the focus directive is necessary to make sure
		// future renders don't cause unexpected focus jumps.
		headerMenuToFocusRef.current = nextHeaderMenuToFocus;
		setNextHeaderMenuToFocus( undefined );
		return;
	}

	const onHide = ( field: NormalizedField< Item > ) => {
		const hidden = headerMenuRefs.current.get( field.id );
		const fallback = hidden
			? headerMenuRefs.current.get( hidden.fallback )
			: undefined;
		setNextHeaderMenuToFocus( fallback?.node );
	};

	const handleHeaderContextMenu = ( event: React.MouseEvent ) => {
		event.preventDefault();
		event.stopPropagation();
		if ( disableHeaderControls ) {
			return;
		}
		const virtualAnchor = {
			getBoundingClientRect: () => ( {
				x: event.clientX,
				y: event.clientY,
				top: event.clientY,
				left: event.clientX,
				right: event.clientX,
				bottom: event.clientY,
				width: 0,
				height: 0,
				toJSON: () => ( {} ),
			} ),
		};
		window.requestAnimationFrame( () => {
			setContextMenuAnchor( virtualAnchor );
		} );
	};

	const hasData = !! data?.length;

	const titleField = fields.find( ( field ) => field.id === view.titleField );
	const mediaField = fields.find( ( field ) => field.id === view.mediaField );
	const descriptionField = fields.find(
		( field ) => field.id === view.descriptionField
	);

	const { showTitle = true, showMedia = true, showDescription = true } = view;
	const hasPrimaryColumn =
		( titleField && showTitle ) ||
		( mediaField && showMedia ) ||
		( descriptionField && showDescription );
	const columns = getTableColumns( view, fields );
	const columnCount =
		columns.length +
		( hasPrimaryColumn ? 1 : 0 ) +
		( hasBulkActions ? 1 : 0 ) +
		( actions?.length ? 1 : 0 );
	const headerMenuRef =
		( column: string, index: number ) => ( node: HTMLButtonElement ) => {
			if ( node ) {
				headerMenuRefs.current.set( column, {
					node,
					fallback: columns[ index > 0 ? index - 1 : 1 ],
				} );
			} else {
				headerMenuRefs.current.delete( column );
			}
		};
	const isInfiniteScroll = view.infiniteScrollEnabled && ! dataByGroup;
	const isRtl = isRTL();
	// Consumer-configured aspect ratio for the primary column's media preview,
	// validated against the presets (like `density`) so arbitrary values are
	// ignored, and surfaced to CSS as a custom property the media stylesheet
	// reads. The property is always set (with the square default), so an
	// identically-named variable set by a consumer on an ancestor can't leak
	// into the previews when the view doesn't configure a ratio. The sizing
	// itself only engages behind the `has-media-aspect-ratio` modifier below.
	const mediaAspectRatio =
		view.layout?.aspectRatio &&
		MEDIA_ASPECT_RATIOS.includes( view.layout.aspectRatio )
			? view.layout.aspectRatio
			: undefined;
	const tableStyle = {
		'--wp-dataviews-media-aspect-ratio': mediaAspectRatio ?? '1/1',
	} as CSSProperties;
	if ( ! hasData && ! isActionInProgress ) {
		return (
			<div
				className={ clsx( 'dataviews-no-results', {
					'is-refreshing': isDelayedLoading,
				} ) }
				id={ tableNoticeId }
			>
				{ empty }
			</div>
		);
	}

	return (
		<>
			{ ( hasBulkActions || isActionInProgress ) && (
				<div
					className={ clsx(
						'dataviews-view-table__bulk-actions-overlay',
						{
							'is-refreshing':
								! isInfiniteScroll && isDelayedLoading,
						}
					) }
					ref={ bulkActionsRef }
					hidden={ ! showBulkActions }
					inert={ inertValue( !! isLoading ) }
				>
					<BulkActionToolbar
						onActionInProgressChange={ setIsActionInProgress }
						selectionCheckboxRef={ bulkSelectionRef }
					/>
				</div>
			) }
			<table
				className={ clsx( 'dataviews-view-table', className, {
					[ `has-${ view.layout?.density }-density` ]:
						view.layout?.density &&
						[ 'compact', 'comfortable' ].includes(
							view.layout.density
						),
					'has-bulk-actions': hasBulkActions,
					'is-refreshing': ! isInfiniteScroll && isDelayedLoading,
					'has-media-aspect-ratio': !! mediaAspectRatio,
				} ) }
				style={ tableStyle }
				aria-busy={ isLoading }
				aria-describedby={ tableNoticeId }
				role={ isInfiniteScroll ? 'feed' : undefined }
				inert={ inertValue( ! isInfiniteScroll && isLoading ) }
			>
				<colgroup>
					{ hasBulkActions && (
						<col className="dataviews-view-table__col-checkbox" />
					) }
					{ hasPrimaryColumn && (
						<col className="dataviews-view-table__col-first-data" />
					) }
					{ columns.map( ( column, index ) => (
						<col
							key={ `col-${ column }` }
							className={ clsx(
								`dataviews-view-table__col-${ column }`,
								{
									'dataviews-view-table__col-expand':
										! hasPrimaryColumn &&
										index === columns.length - 1,
								}
							) }
						/>
					) ) }
					{ !! actions?.length && (
						<col className="dataviews-view-table__col-actions" />
					) }
				</colgroup>
				{ contextMenuAnchor && (
					<Popover
						anchor={ contextMenuAnchor }
						onClose={ () => setContextMenuAnchor( null ) }
						placement="bottom-start"
					>
						<PropertiesSection showLabel={ false } />
					</Popover>
				) }
				<thead
					ref={ tableHeaderRefs }
					className={ clsx( {
						'dataviews-view-table__thead--stuck':
							isVerticallyScrolled,
					} ) }
					onContextMenu={ handleHeaderContextMenu }
				>
					<tr className="dataviews-view-table__row">
						{ hasBulkActions && (
							<th
								className="dataviews-view-table__checkbox-column"
								scope="col"
								onContextMenu={ handleHeaderContextMenu }
							>
								<div
									inert={ inertValue(
										disableHeaderControls
									) }
									aria-hidden={
										disableHeaderControls || undefined
									}
								>
									<BulkSelectionCheckbox
										checkboxRef={ tableSelectionRef }
										selection={ selection }
										onChangeSelection={ onChangeSelection }
										data={ data }
										actions={ actions }
										getItemId={ getItemId }
									/>
								</div>
							</th>
						) }
						{ hasPrimaryColumn && (
							<th
								scope="col"
								aria-sort={
									view.sort?.field === titleField?.id &&
									view.sort
										? sortValues[ view.sort.direction ]
										: undefined
								}
							>
								{ titleField && (
									<ColumnHeaderMenu
										ref={ headerMenuRef(
											titleField.id,
											0
										) }
										fieldId={ titleField.id }
										isCovered={ disableHeaderControls }
										view={ view }
										fields={ fields }
										onChangeView={ onChangeView }
										onHide={ onHide }
										setOpenedFilter={ setOpenedFilter }
										canMove={ false }
										canInsertLeft={
											isRtl
												? ( view.layout?.enableMoving ??
													true )
												: false
										}
										canInsertRight={
											isRtl
												? false
												: ( view.layout?.enableMoving ??
													true )
										}
									/>
								) }
							</th>
						) }
						{ columns.map( ( column, index ) => {
							// Explicit picks the supported styles.
							const { width, maxWidth, minWidth, align } =
								view.layout?.styles?.[ column ] ?? {};
							const field = fields.find(
								( f ) => f.id === column
							);
							const effectiveAlign = getEffectiveAlign(
								align,
								field?.type
							);
							const canInsertOrMove =
								view.layout?.enableMoving ?? true;
							return (
								<th
									key={ column }
									style={ {
										width,
										maxWidth,
										minWidth,
										textAlign: effectiveAlign,
									} }
									aria-sort={
										view.sort?.direction &&
										view.sort?.field === column
											? sortValues[ view.sort.direction ]
											: undefined
									}
									scope="col"
								>
									<ColumnHeaderMenu
										ref={ headerMenuRef( column, index ) }
										fieldId={ column }
										isCovered={ disableHeaderControls }
										view={ view }
										fields={ fields }
										onChangeView={ onChangeView }
										onHide={ onHide }
										setOpenedFilter={ setOpenedFilter }
										canMove={ canInsertOrMove }
										canInsertLeft={ canInsertOrMove }
										canInsertRight={ canInsertOrMove }
									/>
								</th>
							);
						} ) }
						{ !! actions?.length && (
							<th
								className={ clsx(
									'dataviews-view-table__actions-column',
									{
										'dataviews-view-table__actions-column--sticky': true,
										'dataviews-view-table__actions-column--stuck':
											! isHorizontalScrollEnd,
									}
								) }
							>
								<span className="dataviews-view-table-header">
									{ __( 'Actions' ) }
								</span>
							</th>
						) }
					</tr>
				</thead>
				{ /* Render grouped data if groupBy is specified */ }
				{ hasData && groupField && dataByGroup ? (
					Array.from( dataByGroup.entries() ).map(
						( [ groupName, groupItems ] ) => (
							<tbody key={ `group-${ groupName }` }>
								<tr className="dataviews-view-table__group-header-row">
									<td
										colSpan={ columnCount }
										className="dataviews-view-table__group-header-cell"
									>
										{ view.groupBy?.showLabel === false
											? groupName
											: sprintf(
													// translators: 1: The label of the field e.g. "Date". 2: The value of the field, e.g.: "May 2022".
													__( '%1$s: %2$s' ),
													groupField.label,
													groupName
												) }
									</td>
								</tr>
								{ groupItems.map( ( item, index ) => {
									const id =
										getItemId( item ) || index.toString();
									return (
										<TableRow
											key={ getItemId( item ) }
											item={ item }
											level={
												view.showLevels &&
												typeof getItemLevel ===
													'function'
													? getItemLevel( item )
													: undefined
											}
											hasBulkActions={ hasBulkActions }
											actions={ actions }
											fields={ fields }
											id={ id }
											view={ view }
											titleField={ titleField }
											mediaField={ mediaField }
											mediaAspectRatio={
												mediaAspectRatio
											}
											descriptionField={
												descriptionField
											}
											selection={ selection }
											getItemId={ getItemId }
											onChangeSelection={
												onChangeSelection
											}
											{ ...getSelectionProps( id ) }
											onClickItem={ onClickItem }
											renderItemLink={ renderItemLink }
											isItemClickable={ isItemClickable }
											isActionsColumnSticky={
												! isHorizontalScrollEnd
											}
										/>
									);
								} ) }
							</tbody>
						)
					)
				) : (
					<tbody>
						{ hasData &&
							data.map( ( item, index ) => {
								const id =
									getItemId( item ) || index.toString();
								return (
									<TableRow
										key={ getItemId( item ) }
										item={ item }
										level={
											view.showLevels &&
											typeof getItemLevel === 'function'
												? getItemLevel( item )
												: undefined
										}
										hasBulkActions={ hasBulkActions }
										actions={ actions }
										fields={ fields }
										id={ id }
										view={ view }
										titleField={ titleField }
										mediaField={ mediaField }
										mediaAspectRatio={ mediaAspectRatio }
										descriptionField={ descriptionField }
										selection={ selection }
										getItemId={ getItemId }
										onChangeSelection={ onChangeSelection }
										{ ...getSelectionProps( id ) }
										onClickItem={ onClickItem }
										renderItemLink={ renderItemLink }
										isItemClickable={ isItemClickable }
										isActionsColumnSticky={
											! isHorizontalScrollEnd
										}
										posinset={
											isInfiniteScroll
												? index + 1
												: undefined
										}
									/>
								);
							} ) }
					</tbody>
				) }
			</table>
			{ isInfiniteScroll && isLoading && (
				<div className="dataviews-loading" id={ tableNoticeId }>
					<p className="dataviews-loading-more">
						<WCSpinner />
					</p>
				</div>
			) }
		</>
	);
}

export default ViewTable;
